import { formatStatementDate as formatDate } from "../../helpers/clientStatement";
import { formatMoneyFromCents } from "../../helpers/invoiceMoney";
import {
  PDF_COLORS,
  PDF_PAGE_MARGINS,
  PDF_STYLES,
  pdfGoldRule,
} from "../shared/pdfTheme";
import { buildStandardFooter, TABLE_LAYOUT } from "../shared/pdfDocumentParts";

function moneyCell(cents, { blankIfZero = false } = {}) {
  if (blankIfZero && !cents) return "—";
  return formatMoneyFromCents(cents);
}

function brandStack(branding) {
  const stack = [{ text: branding?.brandName || "Organization", style: "brandName" }];
  if (branding?.legalNameLine) {
    stack.push({ text: branding.legalNameLine, style: "companyTagline" });
  }
  (branding?.registrationLines || []).forEach((line) => {
    stack.push({ text: line, style: "companyTagline", fontSize: 7.5 });
  });
  (branding?.contactLines || []).forEach((line) => {
    stack.push({ text: line, style: "companyTagline" });
  });
  return stack;
}

export function buildStatementPdfDocumentDefinition(statement, { logoDataUrl, branding } = {}) {
  if (!statement) {
    throw new Error("Statement is required");
  }

  const clientStack = [
    { text: "CLIENT", style: "panelTitle" },
    { text: statement.clientName || "Client", style: "itemName", margin: [0, 0, 0, 3] },
    ...(statement.clientLines || []).map((line) => ({
      text: line,
      style: "itemDetail",
    })),
  ];

  const summaryRows = [
    ["Opening balance", moneyCell(statement.openingCents)],
    ["Charges", moneyCell(statement.chargesCents)],
    ["Payments / credits", moneyCell(statement.paymentsCents)],
    ["Closing balance", moneyCell(statement.closingCents)],
  ];

  const tableBody = [
    ["Date", "Transaction", "Reference", "Charges", "Payments / credits", "Balance"].map((text) => ({
      text,
      style: "tableHeader",
    })),
    [
      { text: "Opening", italics: true },
      { text: "Balance brought forward", italics: true, colSpan: 4 },
      {},
      {},
      {},
      { text: moneyCell(statement.openingCents), alignment: "right", italics: true },
    ],
    ...(statement.activity || []).map((row) => {
      const transaction = row.projectName
        ? `${row.transaction}\n${row.projectName}`
        : row.transaction;
      return [
        formatDate(row.date),
        transaction,
        row.reference || "—",
        { text: moneyCell(row.chargeCents, { blankIfZero: true }), alignment: "right" },
        { text: moneyCell(row.paymentCents, { blankIfZero: true }), alignment: "right" },
        { text: moneyCell(row.balanceCents), alignment: "right" },
      ];
    }),
    [
      { text: "Closing", bold: true },
      { text: "Balance", bold: true, colSpan: 4 },
      {},
      {},
      {},
      { text: moneyCell(statement.closingCents), alignment: "right", bold: true },
    ],
  ];

  const leftStack = logoDataUrl
    ? {
        columns: [
          { image: logoDataUrl, width: 44, height: 44 },
          { stack: brandStack(branding), margin: [10, 0, 0, 0] },
        ],
      }
    : { stack: brandStack(branding) };

  return {
    pageSize: "LETTER",
    pageMargins: PDF_PAGE_MARGINS,
    defaultStyle: { font: "Roboto", fontSize: 9, color: PDF_COLORS.text },
    styles: PDF_STYLES,
    info: {
      title: `Statement of Account — ${statement.clientName || "Client"}`,
      author: branding?.pdfAuthor || branding?.brandName || "Organization",
    },
    footer: buildStandardFooter(
      formatDate(statement.statementDate),
      branding?.footerText || branding?.brandName || "Organization",
    ),
    content: [
      leftStack,
      {
        text: "STATEMENT OF ACCOUNT",
        style: "docTitle",
        characterSpacing: 1.5,
        fontSize: 16,
        margin: [0, 14, 0, 8],
      },
      pdfGoldRule(),
      {
        columns: [
          { stack: clientStack, width: "*" },
          {
            width: 220,
            stack: [
              { text: `Statement date: ${formatDate(statement.statementDate)}`, alignment: "right" },
              {
                text: `Period: ${formatDate(statement.periodStart)} – ${formatDate(statement.periodEnd)}`,
                alignment: "right",
                margin: [0, 2, 0, 0],
              },
            ],
          },
        ],
        columnGap: 16,
        margin: [0, 12, 0, 12],
      },
      {
        table: {
          widths: ["*", 110],
          body: summaryRows.map(([label, value]) => [
            { text: label, border: [false, false, false, false] },
            { text: value, alignment: "right", border: [false, false, false, false] },
          ]),
        },
        layout: "noBorders",
        margin: [0, 0, 0, 12],
      },
      {
        table: {
          headerRows: 1,
          widths: [62, "*", 78, 62, 78, 62],
          body: tableBody,
        },
        layout: TABLE_LAYOUT,
      },
    ],
  };
}
